import { MenuSearchBoxOption } from '@cdl/heartbeat-react';
import fetchWithOptions from '../../../shared/utils/fetch';
import { URLParams } from '../../../shared/utils/urlParams';
import { getSearchQuery } from '../utils/task.utils';
import {
  AnnotationTask,
  Annotator,
  ServerAnnotationTask,
} from '../../ecg/types/annotation-tasks.type';
import { tagsToList, wavesToList } from '../../auth/clients/preferences.client';
import { FormValues } from '../types/annotation-task-detail.type';

export const loadAnnotationTasks = async (
  status: string,
  currentPage: number,
  startDate: string,
  endDate: string,
  searchInput: string,
  selectedAnnotators: string[],
  selectedGrids: string[],
  selectedEfforts: string[],
): Promise<AnnotationTask[] | null> => {
  try {
    const searchParams = {
      startDate,
      endDate,
      searchInput,
      selectedAnnotators,
      selectedGrids,
      selectedEfforts,
    };

    const taskSearchApiQuery = getSearchQuery(currentPage, {
      ...searchParams,
      status,
    });
    const queryParams = new URLParams(taskSearchApiQuery).toString();
    let annotationTasksList = await fetchWithOptions(
      `/api/annotation/task/search?${queryParams}`,
    );

    if (status === 'paused') {
      const archivedTaskSearchApiQuery = getSearchQuery(currentPage, {
        ...searchParams,
        status: 'archived',
      });
      const archivedTaskQueryParams = new URLParams(
        archivedTaskSearchApiQuery,
      ).toString();
      const annotationTasksListArchived = await fetchWithOptions(
        `/api/annotation/task/search?${archivedTaskQueryParams}`,
      );
      annotationTasksList = [
        ...annotationTasksList,
        ...annotationTasksListArchived,
      ];
    }

    return annotationTasksList.map((task: ServerAnnotationTask) =>
      jsonToAnnotationTask(task),
    );
  } catch {
    return null;
  }
};

const jsonToAnnotationTask = (json: ServerAnnotationTask) => {
  return {
    id: json.id,
    name: json.name,
    nbAnnotations: json.nb_annotations,
    completion: `${json.ecg_done_count}/${json.ecg_total_count}`,
    annotators: json.annotators.map((el: { name: string }) => el.name),
    annotationGrid: json.annotation_grid,
    createdAt: json.created_at,
    eta: json.eta,
    status: json.status,
    effort: json.effort,
    description: json.description,
    ecgDoneCount: json.ecg_done_count,
    ecgPendingCount: json.ecg_pending_count,
    ecgToStartCount: json.ecg_to_start_count,
    ecgTotalCount: json.ecg_total_count,
    ecgPendingDetail: json.ecg_pending_detail,
    userCumulCompletions: json.user_cumul_completions,
    userTotalCompletions: json.user_total_completions,
    userDailyCompletions: json.user_daily_completions,
    userDailyWorktime: json.user_daily_worktime,
    totalDailyCompletions: json.total_completions_per_day,
    remainingDays: json.remaining_days,
    workingDays: json.working_days,
    cumulDonePerDay: json.cumul_ecg_done_per_day,
    averageProductivity: json.productivity_average,
    effectiveDate: json.effective_date,
    settings: {
      wavesToDisplay: wavesToList(
        json.settings.platane_annotation_settings.waves_to_display,
      ),
      wavesToAnnotate: wavesToList(
        json.settings.platane_annotation_settings.waves_to_annotate,
      ),
      tagsToDisplay: tagsToList(
        json.settings.platane_annotation_settings.tags_to_display,
      ),
      tagsToAnnotate: tagsToList(
        json.settings.platane_annotation_settings.tags_to_annotate,
      ),
    },
  };
};

type TaskStatus = 'ongoing' | 'paused' | 'archived';

function isValidStatus(status: string): status is TaskStatus {
  return status === 'ongoing' || status === 'paused' || status === 'archived';
}

export const updateStatus = async (
  annotationTaskID: string,
  status: string,
): Promise<boolean> => {
  try {
    const TASK_STATUS_ENDPOINT: Record<TaskStatus, string> = {
      ongoing: 'resume',
      paused: 'pause',
      archived: 'archive',
    };

    if (!isValidStatus(status)) {
      throw new Error(`Invalid status: ${status}`);
    }

    const task = await fetchWithOptions(
      `/api/annotation/task/${annotationTaskID}/${TASK_STATUS_ENDPOINT[status]}`,
      {
        method: 'POST',
      },
    );

    if (!task) {
      throw new Error(`Failed to fetch`);
    }
    return true;
  } catch {
    return false;
  }
};

export const getAllAnnotationGrid = async (): Promise<
  MenuSearchBoxOption[]
> => {
  try {
    const annotationGrid = await fetchWithOptions('/api/abnormality/grid');
    return annotationGrid.map((item: { grid_name: string }) =>
      jsonToAnnotationGrid(item),
    );
  } catch {
    return [];
  }
};

export const getAllProject = async (): Promise<MenuSearchBoxOption[]> => {
  try {
    const project = await fetchWithOptions('/api/annotation/project');
    return project.map((item: { id: string; name: string }) =>
      jsonToDropdownValue(item),
    );
  } catch {
    return [];
  }
};

export const getAllDataset = async (): Promise<MenuSearchBoxOption[]> => {
  try {
    const dataset = await fetchWithOptions('/api/dataset/all');
    return dataset.map((item: { id: string; name: string }) =>
      jsonToDropdownValue(item),
    );
  } catch {
    return [];
  }
};

export const getAllAnnotators = async (
  withAnnotationTasks: boolean,
): Promise<Annotator[]> => {
  try {
    const queryParam = withAnnotationTasks ? 'with_annotation_tasks=1' : '';
    const annotators = await fetchWithOptions(
      `/api/user/search?${
        queryParam ? `${queryParam}` : 'interfaces[]=platane:true'
      }`,
    );
    return annotators.map((item: { id: string; name: string; email: string }) =>
      jsonToAnnotators(item),
    );
  } catch {
    return [];
  }
};

const jsonToAnnotators = (json: {
  id: string;
  name: string;
  email: string;
}) => {
  return {
    id: json.id,
    name: json.name,
    email: json.email,
  };
};

const jsonToDropdownValue = (json: { id: string; name: string }) => {
  return {
    value: json.id,
    displayValue: `${json.name} (${json.id})`,
  };
};

const jsonToAnnotationGrid = (json: { grid_name: string }) => {
  return {
    value: json.grid_name,
    displayValue: json.grid_name,
  };
};

export const getAnnotationTask = async (
  id: string,
): Promise<AnnotationTask | null> => {
  try {
    const annotationTask = await fetchWithOptions(
      `/api/annotation/task/${id}/metric`,
    );
    return jsonToAnnotationTask(annotationTask);
  } catch {
    return null;
  }
};

export const createAnnotationTask = async (
  values: FormValues,
): Promise<AnnotationTask | null> => {
  try {
    const annotators = values.annotators.map(({ email, ...rest }) => rest);
    const annotationtask = await fetchWithOptions('/api/annotation/task', {
      method: 'POST',
      body: JSON.stringify({
        dataset_id: values.dataset_id,
        name: values.name,
        description: values.description,
        annotation_grid: values.annotation_grid,
        priority: values.priority,
        nb_annotations: values.nb_annotations,
        settings: values.settings,
        annotators: annotators,
        ...(values.project_id.length > 0 && { project_id: values.project_id }),
        ...(values.honeypot_dataset_id.length > 0 && {
          honeypot_dataset_id: values.honeypot_dataset_id,
        }),
        ...(values.honeypot_dataset_id.length > 0 && {
          honeypot_probability: values.honeypot_probability,
        }),
      }),
    });

    return jsonToAnnotationTask(annotationtask);
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Unknown error');
  }
};
